import { useState } from "react";
import { Loader2, Plus, X, Save, Trophy, GripVertical } from "lucide-react";
import {
  usePipelineBoard,
  useEtapas,
  useCreateOportunidad,
  useMoverOportunidad,
  useConvertirOportunidad,
  useDeleteOportunidad,
} from "../api";
import { useContactos } from "../api";
import { useMarketingStore } from "../store";
import { MarketingHeader } from "../components/MarketingHeader";
import type { Oportunidad } from "../types";

function eur(n: number, compact = false): string {
  if (compact && Math.abs(n) >= 1000) {
    return (
      new Intl.NumberFormat("es-ES", { notation: "compact", maximumFractionDigits: 1 }).format(n) + " €"
    );
  }
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
}

export function PipelinePage() {
  const { emisorId } = useMarketingStore();
  const { data: board, isLoading } = usePipelineBoard(emisorId);
  const { data: etapas } = useEtapas(emisorId);
  const { data: contactosData } = useContactos(emisorId);
  const contactos = contactosData?.data;

  const moverMut = useMoverOportunidad();
  const createMut = useCreateOportunidad();
  const convertirMut = useConvertirOportunidad();
  const deleteMut = useDeleteOportunidad();

  const [dragId, setDragId] = useState<number | null>(null);
  const [dragOverEtapa, setDragOverEtapa] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    contacto_id: 0,
    titulo: "",
    valor: 0,
    etapa_id: 0,
    fecha_cierre_estimada: "",
  });

  function onDrop(etapaId: number) {
    if (dragId !== null) {
      moverMut.mutate({ id: dragId, etapaId });
    }
    setDragId(null);
    setDragOverEtapa(null);
  }

  async function saveOp(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId || !form.contacto_id || !form.titulo) return;
    await createMut.mutateAsync({
      emisorId,
      payload: {
        contacto_id: form.contacto_id,
        titulo: form.titulo,
        valor: form.valor,
        etapa_id: form.etapa_id || etapas?.[0]?.id,
        fecha_cierre_estimada: form.fecha_cierre_estimada || null,
      },
    });
    setCreating(false);
    setForm({ contacto_id: 0, titulo: "", valor: 0, etapa_id: 0, fecha_cierre_estimada: "" });
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <MarketingHeader
        title="Marketing"
        subtitle="Pipeline de oportunidades. Arrastra las tarjetas entre etapas."
        right={
          <button
            onClick={() => setCreating(true)}
            disabled={!emisorId}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 font-bold text-sm shadow-soft transition self-end disabled:opacity-50"
          >
            <Plus size={16} /> Nueva oportunidad
          </button>
        }
      />

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {board && (
        <div className="flex-1 min-h-0 overflow-x-auto">
          <div className="flex gap-3 h-full pb-2" style={{ minWidth: "max-content" }}>
            {board.map((col) => (
              <div
                key={col.etapa.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverEtapa(col.etapa.id);
                }}
                onDragLeave={() => setDragOverEtapa(null)}
                onDrop={() => onDrop(col.etapa.id)}
                className={`w-72 shrink-0 flex flex-col rounded-2xl border bg-slate-50/60 ${
                  dragOverEtapa === col.etapa.id
                    ? "border-brand-500 bg-brand-50/50 ring-2 ring-brand-500/20"
                    : "border-slate-200"
                }`}
              >
                <div
                  className="px-3 py-2.5 rounded-t-2xl border-b border-slate-200 flex items-center justify-between"
                  style={{ borderTopColor: col.etapa.color, borderTopWidth: 3 }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ background: col.etapa.color }}
                    />
                    <span className="font-bold text-sm text-slate-800">{col.etapa.nombre}</span>
                    <span className="text-xs text-slate-400">({col.count})</span>
                  </div>
                </div>
                <div className="px-3 py-1.5 text-xs text-slate-500 border-b border-slate-100 bg-white/50">
                  {eur(col.total, true)}
                  {col.total_ponderado !== col.total && (
                    <span className="text-slate-400"> · pond. {eur(col.total_ponderado, true)}</span>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[120px]">
                  {col.oportunidades.map((op) => (
                    <OpCard
                      key={op.id}
                      op={op}
                      esGanado={col.etapa.es_ganado}
                      onDragStart={() => setDragId(op.id)}
                      onConvertir={() => {
                        if (confirm(`¿Convertir "${op.titulo}" en cliente de facturación?`)) {
                          convertirMut.mutate(op.id);
                        }
                      }}
                      onDelete={() => {
                        if (confirm("¿Eliminar oportunidad?")) deleteMut.mutate(op.id);
                      }}
                    />
                  ))}
                  {col.oportunidades.length === 0 && (
                    <div className="text-center text-xs text-slate-400 py-6">Vacío</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={saveOp} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">Nueva oportunidad</h2>
              <button type="button" onClick={() => setCreating(false)} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </header>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Contacto *</label>
                <select
                  required
                  value={form.contacto_id}
                  onChange={(e) => setForm({ ...form, contacto_id: Number(e.target.value) })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value={0}>— elegir —</option>
                  {contactos?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}
                      {c.empresa && c.empresa !== c.nombre ? ` · ${c.empresa}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Título *</label>
                <input
                  required
                  type="text"
                  value={form.titulo}
                  onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Valor €</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.valor || ""}
                    onChange={(e) => setForm({ ...form, valor: Number(e.target.value) })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Etapa</label>
                  <select
                    value={form.etapa_id}
                    onChange={(e) => setForm({ ...form, etapa_id: Number(e.target.value) })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    {etapas?.filter((et) => !et.es_ganado && !et.es_perdido).map((et) => (
                      <option key={et.id} value={et.id}>
                        {et.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Fecha cierre estimada
                </label>
                <input
                  type="date"
                  value={form.fecha_cierre_estimada}
                  onChange={(e) => setForm({ ...form, fecha_cierre_estimada: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCreating(false)}
                className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createMut.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
              >
                <Save size={14} /> Crear
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function OpCard({
  op,
  esGanado,
  onDragStart,
  onConvertir,
  onDelete,
}: {
  op: Oportunidad;
  esGanado: boolean;
  onDragStart: () => void;
  onConvertir: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      className="group rounded-xl border border-slate-200 bg-white p-3 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition"
    >
      <div className="flex items-start gap-1.5">
        <GripVertical size={14} className="text-slate-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-slate-900 truncate">{op.titulo}</div>
          <div className="text-xs text-slate-500 truncate">
            {op.contacto_empresa || op.contacto_nombre}
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="font-mono font-black text-slate-900 text-sm">{eur(op.valor)}</span>
            <span className="text-[10px] font-bold text-slate-400">{op.probabilidad}%</span>
          </div>
          {esGanado && !op.cliente_id && (
            <button
              onClick={onConvertir}
              className="mt-2 w-full inline-flex items-center justify-center gap-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-1 text-xs font-bold"
            >
              <Trophy size={12} /> Convertir en cliente
            </button>
          )}
          {op.cliente_id && (
            <div className="mt-2 text-[10px] font-bold text-emerald-600 text-center">
              ✓ Cliente #{op.cliente_id}
            </div>
          )}
        </div>
        <button
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-600 transition shrink-0"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
